/**
 * Cliente HTTP del SGR (same-origin). Cookie de sesión HttpOnly + cabecera X-CSRF-Token (obtenida de /auth/session)
 * + Idempotency-Key (UUID por acción) en mutaciones financieras. Errores como problem+json (RFC 9457).
 * Nunca se guardan tokens en localStorage.
 */
export type Problem = { type: string; title: string; status: number; detail?: string; instance?: string; errors?: Array<{ loc: string[]; msg: string; type: string }>; [k: string]: unknown };

export class ApiError extends Error {
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
  }
  get status() { return this.problem.status; }
  get code() { return this.problem.type.replace('urn:sgr:error:', ''); }
}

let csrfToken: string | null = null;
export const setCsrfToken = (t: string | null) => { csrfToken = t; };
export const getCsrfToken = () => csrfToken;

type Listener = (status: number) => void;
const listeners = new Set<Listener>();
/** Permite al AppShell reaccionar a 401 (sesión caducada) sin acoplar el cliente al router. */
export const onAuthStatus = (l: Listener) => { listeners.add(l); return () => listeners.delete(l); };

export type RequestOptions = { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown; idempotent?: boolean; /** Clave estable entre reintentos (QA-007): el backend ejecuta una sola vez y reproduce la respuesta. */ idempotencyKey?: string; signal?: AbortSignal; headers?: Record<string, string> };

const BASE = '/api/v1';

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...(opts.headers ?? {}) };
  const init: RequestInit = { method, credentials: 'same-origin', signal: opts.signal, headers };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  if (method !== 'GET') {
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    else if (opts.idempotent) headers['Idempotency-Key'] = crypto.randomUUID();
  }
  const res = await fetch(`${BASE}${path}`, init);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('json') ? await res.json() : await res.text();
  if (!res.ok) {
    if (res.status === 401) listeners.forEach((l) => l(401));
    const problem: Problem = typeof data === 'object' && data && 'status' in data ? (data as Problem) : { type: 'urn:sgr:error:http', title: res.statusText, status: res.status, detail: String(data) };
    throw new ApiError(problem);
  }
  return data as T;
}

export const get = <T,>(path: string, signal?: AbortSignal) => api<T>(path, { signal });
export const post = <T,>(path: string, body?: unknown, idempotent = false) => api<T>(path, { method: 'POST', body, idempotent });
export const put = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body });
export const patch = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body });
export const del = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'DELETE', body });

/** Mensaje legible para el usuario a partir de un error cualquiera. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'version_conflict') return 'Otro usuario modificó este registro. Se actualizó la vista; revisa e intenta de nuevo.';
    if (e.status === 429) return 'Demasiados intentos. Espera un momento e inténtalo de nuevo.';
    if (e.status === 403 && e.code === 'csrf_origin') return `Origen no permitido: el servidor no acepta peticiones desde ${window.location.origin}. Contacta al administrador del SGR.`;
    if (e.status === 403 && e.code === 'csrf_token') return 'La sesión cambió. Recarga la página e inténtalo de nuevo.';
    return e.problem.detail ?? e.problem.title;
  }
  if (e instanceof TypeError) return 'Sin conexión con el servidor. Revisa tu red e inténtalo de nuevo.';
  return 'Ocurrió un error inesperado.';
}
